import type { ReflectionPrompt, UserReflectionView } from '@/models/preformanceManagement';
import { resolveLocaleBase } from '../../../utils/documentLocale';

/**
 * The ONE place the client chooses which language column to show.
 *
 * Per-FIELD fallback, not per-record: a half-translated prompt shows Arabic where it has it and
 * English where it does not (spec §4.6). Matches i18n's fallbackLng: 'en' — the bare column is the
 * English value.
 *
 * resolveLocaleBase normalises `ar-SA` → `ar`, `en-GB` → `en`, and anything unknown → 'en'.
 */
export function pickLocalized(
  base: string | null | undefined,
  ar: string | null | undefined,
  fr: string | null | undefined,
  lang: string | null | undefined,
): string {
  const l = resolveLocaleBase(lang);
  const chosen = l === 'ar' ? ar : l === 'fr' ? fr : base;
  return (chosen && chosen.trim()) ? chosen : (base ?? '');
}

function pickLocalizedList(base: string[], ar: string[], fr: string[], lang: string | null | undefined): string[] {
  const l = resolveLocaleBase(lang);
  const chosen = l === 'ar' ? ar : l === 'fr' ? fr : base;
  return chosen && chosen.length > 0 ? chosen : base;
}

/** What a card actually renders. Numeric id, one string per field, tips always an array. */
export interface ResolvedReflectionPrompt {
  id: number;
  promptId: string;
  title: string;
  category: string;
  question: string;
  subtext: string;
  placeholder: string;
  tips: string[];
  iconName: string;
  displayOrder: number;
}

export function resolvePromptText(p: ReflectionPrompt, lang: string | null | undefined): ResolvedReflectionPrompt {
  return {
    id: p.id,
    promptId: p.promptId,
    title: pickLocalized(p.title, p.titleAr, p.titleFr, lang),
    category: pickLocalized(p.category, p.categoryAr, p.categoryFr, lang),
    question: pickLocalized(p.question, p.questionAr, p.questionFr, lang),
    subtext: pickLocalized(p.subtext, p.subtextAr, p.subtextFr, lang),
    placeholder: pickLocalized(p.placeholder, p.placeholderAr, p.placeholderFr, lang),
    tips: pickLocalizedList(p.tips ?? [], p.tipsAr ?? [], p.tipsFr ?? [], lang),
    iconName: p.iconName || 'Award',
    displayOrder: p.displayOrder,
  };
}

/**
 * A past reflection's prompt title / category in the current language. The server resolves the
 * prompt the answer was written against (status-blind) and sends all three languages; the same
 * per-field fallback as the live cards applies, so history follows a language switch too.
 */
export function pickReflectionTitle(r: Pick<UserReflectionView, 'promptTitle' | 'promptTitleAr' | 'promptTitleFr'>, lang: string | null | undefined): string {
  return pickLocalized(r.promptTitle, r.promptTitleAr, r.promptTitleFr, lang);
}

export function pickReflectionCategory(r: Pick<UserReflectionView, 'promptCategory' | 'promptCategoryAr' | 'promptCategoryFr'>, lang: string | null | undefined): string {
  return pickLocalized(r.promptCategory, r.promptCategoryAr, r.promptCategoryFr, lang);
}

/**
 * The ONE display order, mirroring ReflectionPromptResolver.OrderForDisplay on the server:
 * displayOrder, then a platform default before a company row on a tie, then id (creation
 * order). Used by the employee page and the Operator list so both show prompts in the same sequence.
 */
export function comparePromptOrder(
  a: Pick<ReflectionPrompt, 'id' | 'displayOrder' | 'companyId'>,
  b: Pick<ReflectionPrompt, 'id' | 'displayOrder' | 'companyId'>,
): number {
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
  const aCompany = a.companyId == null ? 0 : 1;
  const bCompany = b.companyId == null ? 0 : 1;
  if (aCompany !== bCompany) return aCompany - bCompany;
  return a.id - b.id;
}

/** The number a NEW prompt gets so it lands after everything the list already holds. */
export function nextDisplayOrder(prompts: ReadonlyArray<Pick<ReflectionPrompt, 'displayOrder'>>): number {
  return prompts.reduce((max, p) => Math.max(max, p.displayOrder), 0) + 1;
}
